import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Toggle } from "@/components/ui/toggle";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Check,
  AlertCircle,
  Loader2,
  CreditCard,
  Shield,
  Clock,
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import BillingAPIService from "@/services/billing-api";
import type { BillingPlan, BillingTier, DiscountCode } from "@/types/billing";
import { useSearchParams } from "react-router-dom";
import { FEATURE_TO_DISPLAY, getFeatureDisplayText } from "@/components/billing/constants";
import { useCountry } from "@/contexts/CountryContext";
import { CountryCode } from "@/types/country";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PlanSelectionProps {
  onPlanSelected: (
    plan: BillingPlan,
    tier: BillingTier,
    options: { discountCode?: string; affiliateCode?: string },
    discount_percent?: number,
  ) => void;
}

const PlanSelection: React.FC<PlanSelectionProps> = ({ onPlanSelected }) => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const {
    getCurrencySymbol,
    formatCurrency,
    currentCountry,
    availableCountries,
    setCurrentCountryCode,
    currentCountryCode,
  } = useCountry();
  const [isLoading, setIsLoading] = useState(true);
  const [plans, setPlans] = useState<{ plans: BillingPlan[] }>({ plans: [] });
  const [selectedPlanId, setSelectedPlanId] = useState<number>();
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">(
    "annual",
  );
  const [discountCode, setDiscountCode] = useState("");
  const [affiliateCode, setAffiliateCode] = useState(
    searchParams.get("ref") || "",
  );
  const [validDiscount, setValidDiscount] = useState<DiscountCode | null>(null);
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false);
  const [discountError, setDiscountError] = useState<string>();
  const [planType, setPlanType] = useState<"parent" | "contractor">("parent");

  // For US users, default to contractor and hide parent option
  const isUSUser = currentCountry?.code === 'US';
  const availablePlanTypes = isUSUser ? ['contractor'] : ['parent', 'contractor'];

  useEffect(() => {
    // Set default plan type based on country
    if (isUSUser) {
      setPlanType('contractor');
      // Pre-populate SELF_EMPLOYED code for US contractor users
      setDiscountCode("SELF_EMPLOYED");
    }
    loadPlans();
  }, [isUSUser, currentCountryCode]);

  // Auto-validate SELF_EMPLOYED code when plans are loaded and we have contractor type
  useEffect(() => {
    if (planType === 'contractor' && discountCode === 'SELF_EMPLOYED' && plans.plans.length > 0 && !validDiscount) {
      handleValidateDiscount();
    }
  }, [plans.plans, planType, discountCode, validDiscount]);

  const loadPlans = async () => {
    try {
      setIsLoading(true);
      const response = await BillingAPIService.getPlans(currentCountryCode);
      setPlans(response);

      // Find the first plan of the current selected type
      const plansOfSelectedType = response.plans.filter(
        (plan) => plan.plan_type === planType,
      );

      if (plansOfSelectedType.length > 0) {
        setSelectedPlanId(plansOfSelectedType[0].id);
      } else if (response.plans.length > 0) {
        setSelectedPlanId(response.plans[0].id);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load plans. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Filter plans based on the selected plan type
  const filteredPlans = plans.plans.filter(
    (plan) => plan.plan_type === planType,
  );

  const getSelectedTier = (planTiers?: BillingTier[]) => {
    if (!planTiers) return undefined;
    
    // Get current country's currency
    const userCurrency = currentCountry?.currencyCode?.toLowerCase() || 'gbp';
    
    // Filter tiers by billing period first
    const periodsMatchingTiers = planTiers.filter((tier) => tier.billing_period === billingPeriod);
    
    if (periodsMatchingTiers.length === 0) {
      return undefined;
    }
    
    if (periodsMatchingTiers.length === 1) {
      // Only one tier for this period, use it
      return periodsMatchingTiers[0];
    }
    
    // Multiple tiers for this period - need to pick the right currency
    // For now, use a simple heuristic based on price ranges:
    // USD prices are typically higher than GBP prices for the same product
    if (userCurrency === 'usd') {
      // For US users, pick the higher-priced tier (likely USD)
      return periodsMatchingTiers.reduce((prev, current) => 
        (current.price > prev.price) ? current : prev
      );
    } else {
      // For non-US users, pick the lower-priced tier (likely GBP)
      return periodsMatchingTiers.reduce((prev, current) => 
        (current.price < prev.price) ? current : prev
      );
    }
  };

  const handleValidateDiscount = async () => {
    if (!discountCode.trim()) return;

    try {
      setIsValidatingDiscount(true);
      setDiscountError(undefined);

      // Get the selected plan name if a plan is selected
      const selectedPlan = selectedPlanId
        ? plans.plans.find((p) => p.id === selectedPlanId)
        : undefined;

      const response = await BillingAPIService.validateDiscountCode(
        discountCode,
        selectedPlan?.name,
      );

      if (response.discount) {
        setValidDiscount(response.discount);

        // Special case for 100% discount - show different message
        if (parseFloat(response.discount.discount_percent) === 100) {
          toast({
            title: "Free Subscription!",
            description: "You've applied a code for a free subscription!",
            variant: "default",
          });
        } else {
          toast({
            title: "Success",
            description: `Discount code applied: ${response.discount.discount_percent}% off`,
          });
        }
      } else {
        setDiscountError("Invalid or expired discount code");
        setValidDiscount(null);
      }
    } catch (error) {
      setDiscountError("Failed to validate discount code");
      setValidDiscount(null);
    } finally {
      setIsValidatingDiscount(false);
    }
  };

  const calculateDiscountedPrice = (price: number): number => {
    if (!validDiscount) return price;
    return price * (1 - parseFloat(validDiscount.discount_percent) / 100);
  };

  const handlePlanTypeChange = (value: string) => {
    const newPlanType = value as "parent" | "contractor";
    setPlanType(newPlanType);

    // For contractor plans, pre-populate SELF_EMPLOYED code
    if (newPlanType === "contractor") {
      setDiscountCode("SELF_EMPLOYED");
      setValidDiscount(null);
      setDiscountError(undefined);
      // Auto-validate the SELF_EMPLOYED code
      setTimeout(() => handleValidateDiscount(), 100);
    } else {
      // Reset discount code validation when switching to parent plans
      setDiscountCode("");
      setValidDiscount(null);
      setDiscountError(undefined);
    }

    // Find the first plan of the new selected type
    const plansOfSelectedType = plans.plans.filter(
      (plan) => plan.plan_type === newPlanType,
    );

    if (plansOfSelectedType.length > 0) {
      setSelectedPlanId(plansOfSelectedType[0].id);
    } else {
      setSelectedPlanId(undefined);
    }
  };

  const handleContinue = () => {
    const selectedPlan = plans.plans.find((p) => p.id === selectedPlanId);
    if (!selectedPlan) return;

    const selectedTier = getSelectedTier(selectedPlan.tiers);
    if (!selectedTier) return;

    onPlanSelected(
      selectedPlan,
      selectedTier,
      {
        discountCode: validDiscount ? discountCode : undefined,
        affiliateCode: affiliateCode || undefined,
      },
      validDiscount ? parseFloat(validDiscount.discount_percent) : undefined,
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-[200px]" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-[500px]" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Hero section with gradient background */}
      <div className="bg-secondary text-secondary-foreground rounded-lg p-8 shadow-lg">
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold mb-4">
            Simplify Your Nanny Payroll Management
          </h1>
          <p className="text-xl opacity-90">
            Professional payroll management designed specifically for household
            employers. Choose the plan that best fits your needs.
          </p>
        </div>
      </div>

      {/* Country Selector */}
      <div className="flex justify-center">
        <div className="w-full max-w-xs space-y-2">
          <Label htmlFor="country-select" className="text-center block text-lg font-medium text-primary">
            Viewing plans for
          </Label>
          <Select
            value={currentCountryCode}
            onValueChange={(value) => setCurrentCountryCode(value as CountryCode)}
          >
            <SelectTrigger id="country-select" className="w-full">
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {availableCountries.map((country) => (
                <SelectItem key={country.code} value={country.code}>
                  <div className="flex items-center">
                    <span>{country.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Plan type tabs - only show for non-US users */}
      {!isUSUser && (
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4 text-primary">
            Are you a parent or a contractor?
          </h2>
          <Tabs
            defaultValue="parent"
            value={planType}
            onValueChange={handlePlanTypeChange}
            className="w-full max-w-md mx-auto"
          >
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="parent" data-testid="plan-type-parent">Parent</TabsTrigger>
              <TabsTrigger value="contractor" data-testid="plan-type-contractor">Contractor</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* US users see contractor-only message */}
      {isUSUser && (
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4 text-primary">
            Contractor Plans for US Users
          </h2>
          <p className="text-lg text-muted-foreground">
            As a US-based user, you can access our contractor services designed specifically for independent contractors.
          </p>
        </div>
      )}

      {/* Features highlights before plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
        <div className="bg-accent p-6 rounded-xl text-center shadow-sm">
          <div className="bg-accent/50 p-3 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <h3 className="font-semibold text-lg mb-2 text-primary">
            Fully Compliant
          </h3>
          <p className="text-muted-foreground">
            {planType === "parent" 
              ? "Tax calculations that meet all legal requirements" 
              : "VAT/GST Calculations that meet all legal requirements"}
          </p>
        </div>

        <div className="bg-secondary/10 p-6 rounded-xl text-center shadow-sm">
          <div className="bg-secondary/20 p-3 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
            <CreditCard className="h-6 w-6 text-primary" />
          </div>
          <h3 className="font-semibold text-lg mb-2 text-primary">
            Easy Payments
          </h3>
          <p className="text-muted-foreground">
            Streamlined payment process for all your requirements
          </p>
        </div>

        <div className="bg-accent p-6 rounded-xl text-center shadow-sm">
          <div className="bg-accent/50 p-3 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
            <Clock className="h-6 w-6 text-primary" />
          </div>
          <h3 className="font-semibold text-lg mb-2 text-primary">
            Time Saving
          </h3>
          <p className="text-muted-foreground">
            Save hours each month with automated calculations
          </p>
        </div>
      </div>

      {/* Enhanced plan cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {filteredPlans.map((plan) => {
          const tier = getSelectedTier(plan.tiers);
          if (!tier) return null;

          const originalPrice = parseFloat(tier.price);
          const discountedPrice = calculateDiscountedPrice(originalPrice);
          const isSelected = selectedPlanId === plan.id;

          // Determine card color based on plan type/selection
          const planColors = isSelected
            ? "bg-accent border-secondary border-2 shadow-md"
            : "bg-card hover:bg-accent/50 transition-colors";

          return (
            <Card
              key={plan.id}
              className={cn(
                "relative flex flex-col h-full overflow-hidden",
                planColors,
                isSelected ? "ring-1 ring-secondary ring-opacity-50" : "",
              )}
              onClick={() => setSelectedPlanId(plan.id)}
              data-testid={`plan-card-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {isSelected && (
                <div className="absolute -top-1 -right-1 bg-secondary text-secondary-foreground p-2 rounded-bl-lg shadow-lg">
                  <Check className="h-5 w-5" />
                </div>
              )}

              {plan.name.toLowerCase().includes("premium") && (
                <div className="absolute top-0 right-0 left-0 bg-secondary text-secondary-foreground text-center py-1 text-sm font-medium">
                  MOST POPULAR
                </div>
              )}

              {/* FREE banner for contractor plans */}
              {plan.plan_type === "contractor" && (
                <div className="absolute top-0 right-0 left-0 bg-blue-600 text-white text-center py-2 text-sm font-bold shadow-lg">
                  🎉 FREE with code SELF_EMPLOYED
                </div>
              )}

              <CardHeader
                className={cn(
                  "pb-2",
                  plan.name.toLowerCase().includes("premium") ? "pt-8" : "",
                  plan.plan_type === "contractor" ? "pt-10" : "",
                )}
              >
                <CardTitle className="text-2xl text-primary">
                  {plan.name}
                </CardTitle>
                <CardDescription className="text-base">
                  {plan.description}
                </CardDescription>
                <div className="mt-4">
                  {/* Special display for contractor plans with SELF_EMPLOYED code */}
                  {plan.plan_type === "contractor" && validDiscount?.code === "SELF_EMPLOYED" && parseFloat(validDiscount.discount_percent) === 100 ? (
                    <div className="space-y-2">
                      <div className="flex items-baseline">
                        <span className="text-4xl font-bold text-blue-600">
                          FREE
                        </span>
                        <span className="text-muted-foreground ml-2">
                          /{billingPeriod === "monthly" ? "mo" : "yr"}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="line-through text-muted-foreground">
                          {formatCurrency(originalPrice)}
                        </span>
                        <span className="ml-2 text-blue-600 font-bold">
                          100% OFF with SELF_EMPLOYED
                        </span>
                      </div>
                    </div>
                  ) : discountedPrice !== originalPrice ? (
                    <div className="space-y-1">
                      <div className="flex items-baseline">
                        <span className="text-4xl font-bold text-primary">
                          {formatCurrency(discountedPrice)}
                        </span>
                        <span className="text-muted-foreground ml-2">
                          /{billingPeriod === "monthly" ? "mo" : "yr"}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="line-through text-muted-foreground">
                          {formatCurrency(originalPrice)}
                        </span>
                        <span className="ml-2 text-secondary font-medium">
                          Save {validDiscount?.discount_percent}%
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <div className="flex items-baseline">
                        <span className="text-4xl font-bold text-primary">
                          {formatCurrency(originalPrice)}
                        </span>
                        <span className="text-muted-foreground ml-2">
                          /{billingPeriod === "monthly" ? "mo" : "yr"}
                        </span>
                      </div>
                      {/* Show hint for contractor plans without discount */}
                      {plan.plan_type === "contractor" && (
                        <div className="text-sm text-blue-600 font-medium">
                          Use code SELF_EMPLOYED for FREE!
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>

              <CardContent className="flex-grow pt-4">
                <ul className="space-y-4">
                  {Object.entries(plan.features)
                    .filter(([feature]) => {
                      // Show feature if it has a display text for the current country
                      const displayText = getFeatureDisplayText(feature, currentCountryCode);
                      return displayText !== feature; // Only show if we have a proper display text
                    })
                    .map(([feature, included]) => (
                      <li key={feature} className="flex items-start">
                        {included ? (
                          <div className="mt-0.5 bg-secondary/20 p-1 rounded-full mr-3">
                            <Check className="h-4 w-4 text-secondary" />
                          </div>
                        ) : (
                          <div className="mt-0.5 bg-muted p-1 rounded-full mr-3">
                            <AlertCircle className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                        <span
                          className={cn(
                            "text-sm",
                            included
                              ? "text-foreground"
                              : "text-muted-foreground",
                          )}
                        >
                          {getFeatureDisplayText(feature, currentCountryCode)}
                        </span>
                      </li>
                    ))}
                </ul>
              </CardContent>

              <CardFooter className="mt-auto pt-6">
                <Button
                  className={cn(
                    "w-full text-base py-6",
                    isSelected
                      ? "bg-secondary text-secondary-foreground hover:bg-secondary/90"
                      : "bg-muted text-muted-foreground hover:bg-muted/70",
                  )}
                  variant={isSelected ? "default" : "outline"}
                  size="lg"
                >
                  {isSelected ? "Selected" : "Select Plan"}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {/* Improved discount and affiliate section */}
      <div className="max-w-md mx-auto space-y-6 border-t border-muted pt-8">
        <h3 className="text-xl font-semibold text-center mb-6 text-primary">
          {planType === "contractor" ? "Get Your FREE Contractor Plan!" : "Extra Savings"}
        </h3>

        <div className="bg-accent p-6 rounded-xl shadow-sm space-y-6">
          {/* Special message for contractor plans */}
          {planType === "contractor" && (
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <div className="flex items-center">
                <span className="text-2xl mr-2">🎉</span>
                <div>
                  <p className="text-blue-800 font-semibold">Self-employed contractors get FREE access!</p>
                  <p className="text-blue-700 text-sm">Use code <strong>SELF_EMPLOYED</strong> below</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-2">
            <Label
              htmlFor="discountCode"
              className="text-sm font-medium text-primary"
            >
              {planType === "contractor" ? "Enter SELF_EMPLOYED for FREE access" : "Discount Code"}
            </Label>
            <div className="flex space-x-2">
              <Input
                id="discountCode"
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
                placeholder="Enter discount code"
                disabled={isValidatingDiscount}
                className="bg-background"
              />
              <Button
                onClick={handleValidateDiscount}
                disabled={!discountCode || isValidatingDiscount}
                variant="outline"
                className="bg-background"
              >
                {isValidatingDiscount && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Apply
              </Button>
            </div>
            {discountError && (
              <Alert variant="destructive" className="mt-2">
                <AlertDescription>{discountError}</AlertDescription>
              </Alert>
            )}
            {validDiscount && (
              <Alert className="bg-secondary/10 border-secondary text-secondary-foreground mt-2">
                <AlertDescription className="flex items-center">
                  <Check className="h-4 w-4 mr-2" />
                  {parseFloat(validDiscount.discount_percent)}% discount
                  applied!
                </AlertDescription>
              </Alert>
            )}
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="affiliateCode"
              className="text-sm font-medium text-primary"
            >
              Affiliate Code
            </Label>
            <Input
              id="affiliateCode"
              value={affiliateCode}
              onChange={(e) => setAffiliateCode(e.target.value)}
              placeholder="Enter affiliate code"
              className="bg-background"
            />
            {affiliateCode && (
              <Alert className="bg-secondary/10 border-secondary text-secondary-foreground mt-2">
                <AlertDescription className="flex items-center">
                  <Check className="h-4 w-4 mr-2" />
                  The person who has referred you will receive {formatCurrency(100)}!
                </AlertDescription>
              </Alert>
            )}
          </div>

          {(validDiscount || affiliateCode) && (
            <Alert className="bg-accent border-secondary/50">
              <AlertDescription className="font-medium text-primary">
                Total savings:{" "}
                {validDiscount && affiliateCode
                  ? `${parseFloat(validDiscount.discount_percent)}% + ${formatCurrency(100)} affiliate discount`
                  : validDiscount
                    ? `${parseFloat(validDiscount.discount_percent)}%`
                    : `${formatCurrency(100)} affiliate discount`}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <Button
          size="lg"
          className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 py-6 text-lg font-medium"
          onClick={handleContinue}
          disabled={!selectedPlanId}
          data-testid="continue-to-checkout"
        >
          Continue to Checkout
        </Button>
      </div>
    </div>
  );
};

export default PlanSelection;
